import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { formatDuration, formatDateTime } from '../lib/api';
import { toast } from 'sonner';
import {
  Users,
  Phone,
  PhoneCall,
  CheckCircle,
  XCircle,
  ChartLineUp,
  Clock,
  ArrowRight,
  ChartLine
} from '@phosphor-icons/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function MetricCard({ label, value, subtitle, icon: Icon, trend }) {
  return (
    <div className="card" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 bg-zinc-800 rounded-sm flex items-center justify-center">
          <Icon size={20} className="text-zinc-400" />
        </div>
        {trend && (
          <span className={`text-xs font-semibold ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="metric-value">{value}</div>
      <div className="metric-label mt-1">{label}</div>
      {subtitle && <div className="text-xs text-zinc-500 mt-1">{subtitle}</div>}
    </div>
  );
}

function RecentCallsTable({ calls }) {
  const getOutcomeBadge = (outcome) => {
    const badges = {
      qualified: 'badge-success',
      closed: 'badge-success',
      not_qualified: 'badge-error',
      callback: 'badge-warning',
      voicemail: 'badge-neutral',
      hung_up: 'badge-error'
    };
    return badges[outcome] || 'badge-neutral';
  };

  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Lead</th>
            <th>Duration</th>
            <th>Outcome</th>
            <th>Date</th>
          </tr>
        </thead>
        <tbody>
          {calls.length === 0 ? (
            <tr>
              <td colSpan="4" className="text-center text-zinc-500 py-8">No calls yet</td>
            </tr>
          ) : (
            calls.map((call) => (
              <tr key={call.id} data-testid={`recent-call-${call.id}`}>
                <td className="font-medium text-zinc-50">{call.leadName || 'Unknown'}</td>
                <td className="font-mono text-zinc-400">{formatDuration(call.durationSeconds)}</td>
                <td>
                  <span className={`badge ${getOutcomeBadge(call.outcome)}`}>
                    {call.outcome || 'pending'}
                  </span>
                </td>
                <td className="text-zinc-500">{formatDateTime(call.createdAt)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, chartsRes, callsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/charts'),
        api.get('/dashboard/recent-calls')
      ]);
      setStats(statsRes.data);
      setCharts(chartsRes.data);
      setRecentCalls(callsRes.data.calls);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="text-zinc-400">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-medium text-zinc-50 tracking-tight">Dashboard</h1>
          <p className="text-zinc-400 mt-1">Overview of your lead calling performance</p>
        </div>
        <Link to="/dashboard/leads" className="btn-primary flex items-center gap-2" data-testid="view-all-leads-btn">
          View All Leads <ArrowRight size={16} />
        </Link>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <MetricCard
          label="Total Leads"
          value={stats?.totalLeads || 0}
          subtitle={`+${stats?.leadsToday || 0} today`}
          icon={Users}
        />
        <MetricCard
          label="Calls Made"
          value={stats?.totalCalls || 0}
          subtitle={`+${stats?.callsToday || 0} today`}
          icon={Phone}
        />
        <MetricCard
          label="Connection Rate"
          value={`${stats?.connectionRate || 0}%`}
          icon={PhoneCall}
        />
        <MetricCard
          label="Qualification Rate"
          value={`${stats?.qualificationRate || 0}%`}
          icon={CheckCircle}
        />
        <MetricCard
          label="Close Rate"
          value={`${stats?.closeRate || 0}%`}
          icon={ChartLineUp}
        />
        <MetricCard
          label="Avg Duration"
          value={formatDuration(stats?.avgDurationSeconds || 0)}
          icon={Clock}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calls Over Time */}
        <div className="card" data-testid="calls-over-time-chart">
          <h3 className="text-lg font-medium text-zinc-50 mb-4">Calls Over Time</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={charts?.callsOverTime || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis 
                  dataKey="date" 
                  stroke="#71717a" 
                  tick={{ fill: '#71717a', fontSize: 12 }}
                  tickFormatter={(v) => v.slice(5)}
                />
                <YAxis stroke="#71717a" tick={{ fill: '#71717a', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#18181b',
                    border: '1px solid #27272a',
                    borderRadius: '4px'
                  }}
                />
                <Line type="monotone" dataKey="completed" stroke="#10b981" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-xs text-zinc-400">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span className="text-xs text-zinc-400">Failed</span>
            </div>
          </div>
        </div>

        {/* Outcomes Breakdown */}
        <div className="card" data-testid="outcomes-breakdown-chart">
          <h3 className="text-lg font-medium text-zinc-50 mb-4">Call Outcomes</h3>
          <div className="h-64 flex items-center justify-center">
            {charts?.outcomesBreakdown?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={charts.outcomesBreakdown}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="outcome"
                  >
                    {charts.outcomesBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '4px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-zinc-500">No call data yet</div>
            )}
          </div>
          <div className="flex flex-wrap gap-4 mt-4 justify-center">
            {charts?.outcomesBreakdown?.map((item, index) => (
              <div key={item.outcome} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                <span className="text-xs text-zinc-400 capitalize">{item.outcome}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lead Sources & Recent Calls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lead Sources */}
        <div className="card" data-testid="lead-sources-chart">
          <h3 className="text-lg font-medium text-zinc-50 mb-4">Lead Sources</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={charts?.leadSources || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                <XAxis type="number" stroke="#71717a" tick={{ fill: '#71717a', fontSize: 12 }} />
                <YAxis 
                  type="category" 
                  dataKey="source" 
                  stroke="#71717a" 
                  tick={{ fill: '#71717a', fontSize: 12 }}
                  width={80}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#18181b',
                    border: '1px solid #27272a',
                    borderRadius: '4px'
                  }}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Calls */}
        <div className="card lg:col-span-2" data-testid="recent-calls-table">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-zinc-50">Recent Calls</h3>
            <Link to="/dashboard/leads" className="text-sm text-blue-400 hover:text-blue-300">
              View all
            </Link>
          </div>
          <RecentCallsTable calls={recentCalls} />
        </div>
      </div>

      {/* Leads by Status */}
      <div className="card" data-testid="leads-by-status">
        <h3 className="text-lg font-medium text-zinc-50 mb-4">Leads by Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {charts?.leadsByStatus?.map((item) => (
            <div key={item.status} className="text-center p-4 bg-zinc-900 rounded-sm">
              <div className="text-2xl font-mono text-zinc-50">{item.count}</div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1 capitalize">
                {item.status?.replace('_', ' ')}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
